// Operations → Automation. What runs without being asked, and whether it ran.
//
// The scheduler is the honest subject here: ten jobs register on a shared
// scheduler, and `schedulers.py` wraps every registration block in try/except, so
// a dropped registration logs a warning and the application still boots. That
// makes "did it register" and "did it error recently" two different questions,
// and both are worth a panel.
import React from "react";

import PageHead from "../../components/PageHead";
import Panel, { Rows, StatusRows } from "./Panel";
import { useEndpoint } from "./useEndpoint";

export default function Automation() {
  const health = useEndpoint("/schedulers/health");
  const errors = useEndpoint("/schedulers/recent-errors");

  const jobs = Array.isArray(health.data?.jobs)
    ? health.data.jobs
    : Array.isArray(health.data?.schedulers)
    ? health.data.schedulers
    : Array.isArray(health.data)
    ? health.data
    : [];
  const errorRows = Array.isArray(errors.data?.errors)
    ? errors.data.errors
    : Array.isArray(errors.data)
    ? errors.data
    : [];

  return (
    <>
      <PageHead
        title="Automation"
        subtitle="What runs without being asked, and whether it ran."
      />

      <div className="grid op-grid">
        <Panel
          title="Scheduled jobs"
          state={health}
          empty={jobs.length === 0}
          note="/api/schedulers/health reported no registered jobs. A registration that failed logs a warning and does not stop the boot, so an empty list here is worth reading as a signal."
        >
          <StatusRows
            items={jobs.slice(0, 20).map((j, i) => [
              String(j?.job_id ?? j?.id ?? j?.name ?? `job ${i + 1}`),
              String(j?.status ?? j?.state ?? j?.next_run_time ?? "—"),
            ])}
          />
        </Panel>

        <Panel
          title="Recent errors"
          state={errors}
          empty={errorRows.length === 0}
          note="No scheduler errors recorded. /api/schedulers/recent-errors returned an empty set."
        >
          <StatusRows
            items={errorRows.slice(0, 20).map((e, i) => [
              String(e?.job_id ?? e?.id ?? `error ${i + 1}`),
              String(e?.message ?? e?.error ?? "—"),
            ])}
          />
        </Panel>
      </div>
    </>
  );
}
